import React, { useContext } from 'react';
import { useTheme } from '@emotion/react';
import { useMediaQuery } from 'react-responsive';
import type { EntriesDetails } from '@betterpool/api-types/contests-service';
import { ContestStatus } from '@betterpool/api-types/contests-service';
import useTranslation from 'next-translate/useTranslation';

import { UserContext } from '~/components/providers/UserProvider';
import ContestInvite from '~/domains/contest/domains/invite/ContestInvite';
import useContestInvite from '~/domains/contest/domains/invite/hooks/useContestInvite';
import { PageHeaderActions } from '~/domains/common/components/PageHeader/PageHeader';
import dt from '~/testing';
import { useRedirectToRef } from '~/domains/common/hooks/useRedirectToRef';
import { useContest } from '~/domains/contest/hooks/useContest';
import { Button } from '~/domains/design-system/Button';
import { FEATURE } from '~/common/enums/feature.enum';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import { useCopyContestLink } from '../../../../hooks/useCopyContestLink';
import EntryButton from '../EntryButton/EntryButton';
import ContestRowMenu from '../../../../../commissioner/common/Contests/ContestRowMenu';
import ViewContestButton from '../EntryButton/ViewContestButton';
import EditContestButton from '../EntryButton/EditContestButton';
import EditEntrantsButton from '../EntryButton/EditEntrantsButton';

import useStyles from './ContestHeader.styles';

export type ContestHeaderActionsProps = {
  contestId: string;
  contestName: string;
  isAfterDeadline: boolean;
  entries: EntriesDetails;
  status: ContestStatus;
  isEntertainmentOnly?: boolean;
  isAllowedInLocation?: boolean;
  isSidePanel?: boolean;
  isContestCommissioner?: boolean;
  hideCTA?: boolean;
};

export function ContestHeaderActions({
  contestId,
  contestName,
  entries,
  isAfterDeadline,
  status,
  isEntertainmentOnly,
  isAllowedInLocation,
  isSidePanel,
  isContestCommissioner,
  hideCTA,
}: ContestHeaderActionsProps) {
  const theme = useTheme();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });
  const { classes } = useStyles({ isSidePanel });
  const { isInviteCTAShown } = useContestInvite();
  const { user } = useContext(UserContext);
  const contest = useContest();

  const { t } = useTranslation('contest');
  const { handleCopyLink } = useCopyContestLink(contestId, contestName);
  const redirectToRef = useRedirectToRef({ replace: true });
  const hasUserEntries = entries.user > 0;
  const isButtonHidden = !hideCTA && !isSidePanel;
  const hasEditPermission = isContestCommissioner && status === ContestStatus.SCHEDULED;
  const isSpectatorMode = contest.data?.public_viewing;
  const isEntrantManagerEnabled =
    useIsFeatureEnabled(FEATURE.ENABLE_ENTRANT_MANAGER) && contest?.data?.manage_entrants;
  const isFirstSlatePicksheetReady = contest.data?.slates[0]?.isPicksheetAvailable;

  return (
    <PageHeaderActions isSidePanel={isSidePanel}>
      {isButtonHidden && !hasUserEntries ? (
        <EntryButton
          className={classes.action}
          contestId={contestId}
          isAfterDeadline={isAfterDeadline}
          entries={entries}
          isAllowedInLocation={isAllowedInLocation}
          isEntertainmentOnly={isEntertainmentOnly}
          status={status}
          isSpectatorMode={isSpectatorMode}
          isFirstSlatePicksheetReady={isFirstSlatePicksheetReady}
        />
      ) : null}
      {(contest.data?.status !== ContestStatus.SCHEDULED && isSpectatorMode) ||
      (isButtonHidden && hasUserEntries) ? (
        <ViewContestButton className={classes.action} contest={contest?.data} />
      ) : null}
      {contest.data?.status === ContestStatus.SCHEDULED && hasEditPermission ? (
        <EditContestButton isHidden={isTablet} className={classes.action} contest={contest?.data} />
      ) : null}
      {isContestCommissioner && isEntrantManagerEnabled ? (
        <EditEntrantsButton
          isHidden={isTablet}
          className={classes.action}
          contest={contest?.data}
        />
      ) : null}
      {!isSpectatorMode &&
        (isInviteCTAShown && user?.id === contest?.data?.commissioner.id ? (
          <ContestInvite
            contestId={contestId}
            contestName={contestName}
            className={classes.action}
          />
        ) : (
          <Button
            variant="secondary-stroke"
            size="medium"
            className={classes.action}
            data-test-id={dt.contestDetail.page.header.actions.copy}
            onClick={handleCopyLink}
          >
            {t('detail.header.actions.copy')}
          </Button>
        ))}
      {isContestCommissioner && !isTablet && !isSidePanel && (
        <ContestRowMenu
          hasEntries={entries.filled > 0}
          contestId={contestId}
          contestName={contestName}
          onSuccess={() => redirectToRef()}
        />
      )}
    </PageHeaderActions>
  );
}
